import { useCallback, useRef } from 'react';
import { useToggle } from 'react-use';
import cn from 'classnames';
import { ShiftButton, UnblockShiftPopover } from '@lp/features/Scheduler/components';
import { OMSShiftCellDropdown } from '@lp/features/Scheduler/SchedulerOMS/components';
import {
  getOMSShiftSlotPrimaryText,
  getOMSShiftSlotColors,
  isDualSlotShiftType,
  getOMSShowHrIcon,
} from '@lp/features/Scheduler/SchedulerOMS/utils';
import { ShiftSiblingData } from '@lp/features/Scheduler/types';
import { OMSDualShiftCell } from '@lp/features/Scheduler/SchedulerOMS/components/OMSShiftCell/components';
import { singleOMSShiftTypes } from '@lp/features/Scheduler/SchedulerOMS';
import { OMSShift, OMSShiftSelectPayload, OMSShiftType, ShiftFixationType } from '@/api/apis/resources/schedule';

type OMSShiftCellProps = {
  shiftData?: OMSShift;
  siblingData?: ShiftSiblingData;
  onShiftChange: (shiftPayload?: OMSShiftSelectPayload) => void;
  lighten?: boolean;
  readonly?: boolean;
  canReset?: boolean;
  isCentered?: boolean;
  getResettableSlot?: (slotIndex: number) => boolean;
  canUnblock?: boolean;
  onUnblock?: () => void;
};

export const OMSShiftCell = ({
  shiftData,
  siblingData,
  onShiftChange,
  lighten,
  readonly,
  canReset,
  isCentered,
  getResettableSlot,
  canUnblock,
  onUnblock,
}: OMSShiftCellProps) => {
  const elRef = useRef<HTMLButtonElement>(null);
  const [isOpen, toggleIsOpen] = useToggle(false);

  const shiftType = shiftData?.slot1?.shiftType || shiftData?.slot2?.shiftType;
  const isDual = isDualSlotShiftType(shiftType);
  const isBlockedRequired = !shiftData?.isEditable && shiftData?.fixationType === ShiftFixationType.Required;
  const disabled = !shiftData?.isEditable || readonly;
  const isSingle = singleOMSShiftTypes.includes(shiftType as OMSShiftType);
  const showContent = !siblingData?.hasPrev;

  const canShowUnblock = isBlockedRequired && canUnblock && !readonly;

  const onClick = useCallback(() => {
    if (!disabled || canShowUnblock) {
      toggleIsOpen();
    }
  }, [disabled, canShowUnblock, toggleIsOpen]);

  const renderShiftButton = () => {
    if (isDual) {
      return (
        <OMSDualShiftCell
          ref={elRef}
          shiftData={shiftData}
          lighten={lighten}
          disabled={disabled && !canShowUnblock}
          onClick={onClick}
        />
      );
    }

    if (shiftData?.slot1) {
      const colors = getOMSShiftSlotColors(shiftData.slot1, shiftData.shiftStatus, shiftData.isPreSchedule);
      const primaryText = getOMSShiftSlotPrimaryText(shiftData.slot1);

      return (
        <ShiftButton
          ref={elRef}
          lighten={lighten}
          disabled={disabled && !canShowUnblock}
          isCentered={isCentered || isSingle}
          siblingData={siblingData}
          className={cn(colors.bg, colors.text, colors.border)}
          onClick={onClick}
          showHrIcon={getOMSShowHrIcon(shiftData)}
        >
          {showContent && (
            <div className="flex flex-col items-start">
              <span>{primaryText}</span>
            </div>
          )}
        </ShiftButton>
      );
    }

    return null;
  };

  return (
    <>
      {renderShiftButton()}

      {isOpen && canShowUnblock && onUnblock && (
        <UnblockShiftPopover triggerRef={elRef} isOpen={isOpen} onOpenChange={toggleIsOpen} onUnblock={onUnblock} />
      )}

      {isOpen && !canShowUnblock && (
        <OMSShiftCellDropdown
          triggerRef={elRef}
          isOpen={isOpen}
          onOpenChange={toggleIsOpen}
          shiftData={shiftData}
          onShiftChange={onShiftChange}
          canReset={canReset}
          getResettableSlot={getResettableSlot}
        />
      )}
    </>
  );
};
